import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { 
  LayoutDashboard, 
  Users, 
  LogOut,
  ChevronRight,
  ExternalLink,
  Menu,
  X,
  BarChart3,
  MessageCircle,
  CreditCard,
  Wallet,
  Inbox,
  Send,
  Mail,
  Sparkles,
  Heart,
  Lock,
  Film
} from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a4b1710a-e8aa-40f2-90ed-6aa15c96d321/artifacts/w14lowsf_Disen%CC%83o%20sin%20ti%CC%81tulo.png";

const menuItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
  { icon: Users, label: "Creadoras", path: "/admin/creadoras" },
  { icon: BarChart3, label: "Estadísticas", path: "/admin/estadisticas" },
  { icon: Inbox, label: "Leads", path: "/admin/leads" },
  { icon: Mail, label: "Boletín", path: "/admin/boletin" },
  { icon: Wallet, label: "Métodos de Pago", path: "/admin/metodos-pago" },
  { icon: MessageCircle, label: "Telegram VIP", path: "/admin/telegram-vip" },
  { icon: Inbox, label: "Mensajes", path: "/admin/mensajes" },
  { icon: Heart, label: "JGFans", path: "/admin/jgfans" },
  { icon: Film, label: "Shorts", path: "/admin/shorts" },
  { icon: Send, label: "Auto-Posting", path: "/admin/auto-posting" },
  { icon: Lock, label: "Bóveda", path: "/admin/boveda" },
  { icon: Sparkles, label: "Bóveda Captions", path: "/admin/captions" },
  { icon: CreditCard, label: "Membresías", path: "/admin/membresias" },
];

export const AdminSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const closeSidebar = () => setIsOpen(false);

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-[#0a0a0a] border-b border-white/10 flex items-center justify-between px-4 z-50">
        <Link to="/admin" className="flex items-center">
          <img src={LOGO_URL} alt="JGlinks" className="h-8 w-auto" />
        </Link>
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 text-white/70 hover:text-white"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Spacer */}
      <div className="md:hidden h-14" />

      {/* Overlay */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-full w-64 bg-[#0a0a0a] border-r border-white/10 flex flex-col z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        {/* Logo - Hidden on mobile (shown in header) */}
        <div className="hidden md:block p-6 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="JGlinks" className="h-12 w-auto" />
          </Link>
          <p className="text-xs text-white/40 mt-2">Panel de Administración</p>
        </div>

        {/* Mobile close button area */}
        <div className="md:hidden p-4 border-b border-white/10 flex items-center justify-between">
          <span className="text-sm text-white/50">Menú</span>
          <button onClick={closeSidebar} className="p-1 text-white/50">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== "/admin" && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive 
                    ? "bg-gradient-to-r from-slate-500/20 to-blue-500/20 text-white border border-slate-500/30" 
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 space-y-3">
          <a 
            href="/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Ver sitio público
          </a>
          
          <Button 
            variant="ghost" 
            className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5 rounded-xl"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-3" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
